/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import React, { useMemo, useState, useCallback } from 'react';
import clsx from 'clsx';
import { AIcon, AIconSize } from '@marriott/mi-web-ui-kit';
import { Modal } from '@marriott/mi-ui-library';
import {
  createDecimalValue,
  getExchangeConversionRate,
  getRateConversionFactor,
  formatOpeningSoonDate,
} from '../../modules/utils';
import { usePageContext } from '../../context/PageContext';
import { getCurrentPriceRateBlock, getRateBlock } from '../../organisms/OSearchResults/lib/helper';
import { formatMandatoryFeeForDisplay, getHotelCodeUnavailable, hotelUnavailableOrMaxOccupied } from '../../modules/utils/ratesHelper';
import { buildSearchQueryDataFromAmpRequest } from '../../utils/ampRequestMapper';
import { isDateAfterToday } from '../../utils/utils';
import * as Styled from './MPropertyPriceSection.styles';
import type {
  RateBlock,
  LowestAverageRate,
  BasicInformation,
  MPropertyPriceSectionProps,
  FeeModalProps,
  PriceInfo,
  MandatoryFeeTypeResult,
  DisplayMode,
  AmpRequest,
  EEOPricingInfo,
} from './MPropertyPriceSection.types';
import {
  STANDARD_RATES_CODE,
  SPECIAL_RATES_CODE,
  P17_CODE,
  MRW_CODE,
  EEO_MEMBER_CODES,
  STRIKE_PRICE_THRESHOLD_PERCENTAGE,
} from './MPropertyPriceSection.constants';
import { formatNumberByLocale } from '@marriott/mi-chat-components';
import { EEO_TYPE } from '../../modules/constants';

export type { DisplayMode };

/**
 * Extract the rateRequestValue from ampRequest object
 * This value is used to match against rateCategory.value in rate blocks
 */
function getRateRequestValue(ampRequest?: AmpRequest): string | null {
  const rateRequests = ampRequest?.productQueriesHospitality?.priceModifiers?.rateRequest;
  if (!rateRequests || rateRequests.length === 0) return null;
  return rateRequests[0]?.rateRequestValue || null;
}

/**
 * Find a rate block matching the rateRequestValue from ampRequest
 * Returns the matching rate block or null if not found
 */
function findRateBlockByRequestValue(rates: RateBlock[], rateRequestValue: string): RateBlock | null {
  return (
    rates.find(rateBlock => rateBlock.rateCategory?.value?.toLowerCase() === rateRequestValue.toLowerCase()) || null
  );
}

/**
 * Check if a rate block is an EEO (Elite Exclusive Offer) rate
 * EEO rates have specific cluster codes: E0P, E0S, E0G
 */
function isEEORateBlock(rateBlock: RateBlock): boolean {
  return (
    rateBlock.rateCategory?.code === SPECIAL_RATES_CODE &&
    rateBlock.rateCategory?.value !== null &&
    rateBlock.rateCategory?.value !== undefined &&
    EEO_MEMBER_CODES.includes(rateBlock.rateCategory.value)
  );
}

/**
 * Get the standard rate block from the rates array
 */
function getStandardRateBlock(rates: RateBlock[]): RateBlock | null {
  return (
    rates.find(
      rateBlock => rateBlock.rateCategory?.value === null && rateBlock.rateCategory?.code === STANDARD_RATES_CODE
    ) || null
  );
}

/**
 * Get the EEO rate block from the rates array
 * Returns the first matching EEO rate block (E0P, E0S, or E0G)
 */
function getEEORateBlock(rates: RateBlock[]): RateBlock | null {
  return rates.find(rateBlock => isEEORateBlock(rateBlock)) || null;
}

/**
 * Get the numeric amount from a rate for comparison
 * Uses amountPlusMandatoryFees if available, otherwise falls back to amount
 */
function getRateNumericAmount(rate: LowestAverageRate | null | undefined): number {
  if (!rate) return Infinity;
  const amountData = rate.amountPlusMandatoryFees?.amount ? rate.amountPlusMandatoryFees : rate.amount;
  if (!amountData?.amount || typeof amountData.decimalPoint !== 'number') return Infinity;
  return createDecimalValue(amountData.amount, amountData.decimalPoint);
}

/**
 * Check if the price difference meets the threshold percentage for strikethrough display
 * Following the reference: threshold is 5% difference
 */
function checkThresholdPercentage(standardPrice: number, eeoPrice: number): boolean {
  if (standardPrice <= 0 || eeoPrice <= 0) return false;
  const percentage = (standardPrice * STRIKE_PRICE_THRESHOLD_PERCENTAGE) / 100;
  const difference = standardPrice - eeoPrice;
  return difference >= percentage;
}

/**
 * Get EEO pricing information including display rate and strikethrough rate
 * Implements the logic to compare standard and EEO rates and determine which to display
 *
 * Logic:
 * 1. If only one rate block exists, use it as the display rate
 * 2. If both standard and EEO rates exist:
 *    - Compare the rates and display the lowest one
 *    - If EEO rate is lower and meets threshold, show strikethrough with standard rate
 */
function getEEOPricingInfo(rates?: RateBlock[], rateRequestValue?: string | null): EEOPricingInfo | null {
  if (!rates || rates.length === 0) return null;

  // If rateRequestValue is provided, try to find a matching rate block first
  // Only return early for non-EEO special rates (e.g., corporate codes, group codes)
  // EEO rates should fall through to the Standard vs EEO comparison logic
  // to match PropertyCard behavior (show StandardRates unless 5% threshold is met)
  if (rateRequestValue) {
    const matchingBlock = findRateBlockByRequestValue(rates, rateRequestValue);
    if (matchingBlock?.rateModes?.lowestAverageRate && !isEEORateBlock(matchingBlock)) {
      return {
        displayRate: matchingBlock.rateModes.lowestAverageRate,
        strikeThroughRate: null,
        showStrikeThrough: false,
        isEEORate: false,
      };
    }
  }

  const standardRateBlock = getStandardRateBlock(rates);
  const eeoRateBlock = getEEORateBlock(rates);

  const standardRate = standardRateBlock?.rateModes?.lowestAverageRate;
  const eeoRate = eeoRateBlock?.rateModes?.lowestAverageRate;

  // If only standard rate exists
  if (standardRate && !eeoRate) {
    return {
      displayRate: standardRate,
      strikeThroughRate: null,
      showStrikeThrough: false,
      isEEORate: false,
    };
  }

  // If only EEO rate exists
  if (eeoRate && !standardRate) {
    return {
      displayRate: eeoRate,
      strikeThroughRate: null,
      showStrikeThrough: false,
      isEEORate: true,
    };
  }

  // If both rates exist, show StandardRates unless EEO threshold is met (matching PropertyCard behavior)
  if (standardRate && eeoRate) {
    const standardAmount = getRateNumericAmount(standardRate);
    const eeoAmount = getRateNumericAmount(eeoRate);

    // Only switch to EEO if it's lower AND the 5% threshold is met
    if (eeoAmount < standardAmount) {
      const thresholdMet = checkThresholdPercentage(standardAmount, eeoAmount);
      if (thresholdMet) {
        // Threshold met: show EEO as display rate, StandardRates as strikethrough
        return {
          displayRate: eeoRate,
          strikeThroughRate: standardRate,
          showStrikeThrough: true,
          isEEORate: true,
        };
      }
    }

    // Threshold NOT met OR standard is lower/equal: show StandardRates without strikethrough
    return {
      displayRate: standardRate,
      strikeThroughRate: null,
      showStrikeThrough: false,
      isEEORate: false,
    };
  }

  // Fallback to first available rate
  const fallbackRate = rates[0]?.rateModes?.lowestAverageRate;
  if (fallbackRate) {
    return {
      displayRate: fallbackRate,
      strikeThroughRate: null,
      showStrikeThrough: false,
      isEEORate: isEEORateBlock(rates[0]),
    };
  }

  return null;
}

const FeeModal: React.FC<FeeModalProps> = ({
  isOpen,
  onClose,
  modalTitle,
  description,
  includesLabel = 'Includes',
  viewRatesLabel = 'View Rates',
  viewRatesURL = '',
}) => {
  const toolTipList = description ? description.split('|') : [];

  if (!isOpen) return null;

  return (
    <Modal
      secondaryClassName={clsx('popup-width', 'custom-position')}
      show={true}
      handleBlur={true}
      popupOpenState={isOpen}
      setPopupOpenState={onClose}
      role="dialog"
      labelledBy="tooltip-modal"
      modalId="tooltip-modal"
      usePortal={true}
    >
      <div className="tooltip-popup-wrapper" onClick={e => e.stopPropagation()}>
        <Modal.Header
          className="custom-header-font"
          labelText={modalTitle}
          popupHeaderOnCLoseFunc={onClose}
          popupCloseClassName="popup-close-button"
          flexLayout={true}
        ></Modal.Header>
        <Modal.Body className="custom-heights" flexLayout={true}>
          <div className="tooltip-main-wrapper" tabIndex={0} role="document">
            {includesLabel}:
            <div className="tooltip-main-content">
              <ul className={clsx('custom-ul-props', toolTipList?.length === 1 ? 'full-width-list' : '')}>
                {toolTipList?.map((item: string, index: number) => {
                  return (
                    <li key={index} className="custom-list">
                      {item?.replace('•', '')}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          setFeatureSelected={undefined}
          viewRateLabel={viewRatesLabel}
          viewRatesURL={viewRatesURL}
          className="color-scheme3 row-reverse"
          fromToolTip={true}
          flexLayout={true}
          viewRatesOpenInNewTab
        ></Modal.Footer>
      </div>
    </Modal>
  );
};

// function formatPriceValueNumeric(amount: number, decimalPoint: number): number {
//   return Math.round(amount / Math.pow(10, decimalPoint));
// }

function formatPointsValue(points: number): string {
  return new Intl.NumberFormat('en-US').format(points);
}


/**
 * Get points value and check if it's a Points Saver (P17) rate
 * Returns both the points value and whether it's a Points Saver rate
 * If rateRequestValue is provided, prioritize matching rate block
 */
function getPointsInfo(
  rates?: RateBlock[],
  rateRequestValue?: string | null
): { points: number | null; isPointsSaver: boolean } {
  if (!rates || rates.length === 0) return { points: null, isPointsSaver: false };

  // If rateRequestValue is provided, try to find a matching rate block with points first
  if (rateRequestValue) {
    const matchingBlock = findRateBlockByRequestValue(rates, rateRequestValue);
    if (matchingBlock?.rateModes?.pointsPerUnit?.points) {
      const isPointsSaver = matchingBlock.rateCategory?.value === P17_CODE;
      return { points: matchingBlock.rateModes.pointsPerUnit.points, isPointsSaver };
    }
  }

  // First try to find P17 (Points Saver) rate block - this takes priority
  const p17Block = rates.find(
    rateBlock =>
      rateBlock.rateCategory?.code === SPECIAL_RATES_CODE &&
      rateBlock.rateCategory?.value === P17_CODE &&
      rateBlock.rateModes?.pointsPerUnit?.points
  );

  if (p17Block?.rateModes?.pointsPerUnit?.points) {
    return { points: p17Block.rateModes.pointsPerUnit.points, isPointsSaver: true };
  }

  // Try to find MRW (Marriott Rewards) rate block
  const mrwBlock = rates.find(
    rateBlock =>
      rateBlock.rateCategory?.code === SPECIAL_RATES_CODE &&
      rateBlock.rateCategory?.value === MRW_CODE &&
      rateBlock.rateModes?.pointsPerUnit?.points
  );

  if (mrwBlock?.rateModes?.pointsPerUnit?.points) {
    return { points: mrwBlock.rateModes.pointsPerUnit.points, isPointsSaver: false };
  }

  // Fall back to any other special rates block with points
  const pointsBlock = rates.find(
    rateBlock => rateBlock.rateCategory?.code === SPECIAL_RATES_CODE && rateBlock.rateModes?.pointsPerUnit?.points
  );

  return {
    points: pointsBlock?.rateModes?.pointsPerUnit?.points || null,
    isPointsSaver: false,
  };
}

/**
 * Identify the type of mandatory fee from property basic information
 * Matches the logic from identifyTypeOfMandatoryFee in ratesHelper.ts
 */
function identifyMandatoryFeeType(basicInfo?: BasicInformation): MandatoryFeeTypeResult {
  const result = {
    isResort: false,
    isDestinationFee: false,
    isLACountyFee: false,
    isMarketingFee: false,
  };

  if (!basicInfo) return result;

  // Check resort flag first
  if (basicInfo.resort) {
    result.isResort = true;
  }

  // Check descriptions for fee type
  const descriptions = basicInfo.descriptions;
  if (descriptions) {
    for (const desc of descriptions) {
      const enumCode = desc?.type?.enumCode;
      if (enumCode === 'RESORT_FEE_DESCRIPTION') {
        result.isResort = true;
      } else if (enumCode === 'DESTINATION_FEE_DESCRIPTION') {
        result.isDestinationFee = true;
      } else if (enumCode === 'SURCHARGE_ORDINANCE_COST_DESCRIPTION') {
        result.isLACountyFee = true;
      } else if (enumCode === 'TOURISM_MARKETING_FEE_DESCRIPTION') {
        result.isMarketingFee = true;
      }
    }
  }

  return result;
}

/**
 * Get the fee label based on fee type
 * Matches the logic from constructToolTipData in ratesHelper.ts
 */
function getFeeLabel(feeTypes: MandatoryFeeTypeResult): string {
  const { isResort, isDestinationFee, isLACountyFee, isMarketingFee } = feeTypes;

  if (isResort) return 'Resort';
  if (isLACountyFee) return 'Surcharge';
  if (isMarketingFee) return 'Tourism Marketing';
  if (isDestinationFee) return 'Destination';

  return '';
}

/**
 * Get the modal title for the fee type
 */
function getFeeModalTitle(feeTypes: MandatoryFeeTypeResult): string {
  const { isResort, isDestinationFee, isLACountyFee, isMarketingFee } = feeTypes;

  if (isResort) return 'Daily Resort Fee';
  if (isLACountyFee) return 'Surcharge For Ordinance Cost';
  if (isMarketingFee) return 'Daily Tourism Marketing Fee';
  if (isDestinationFee) return 'Daily Destination Fee';

  return '';
}

function getFeeDescription(basicInfo?: BasicInformation): string | null {
  if (!basicInfo?.descriptions) return null;

  for (const desc of basicInfo.descriptions) {
    const enumCode = desc?.type?.enumCode;
    if (
      enumCode === 'RESORT_FEE_DESCRIPTION' ||
      enumCode === 'DESTINATION_FEE_DESCRIPTION' ||
      enumCode === 'SURCHARGE_ORDINANCE_COST_DESCRIPTION' ||
      enumCode === 'TOURISM_MARKETING_FEE_DESCRIPTION'
    ) {
      return desc.text || null;
    }
  }

  return null;
}

export const MPropertyPriceSection: React.FC<MPropertyPriceSectionProps> = ({
  edge,
  priceDisplay,
  showDivider = true,
  displayMode = 'cash',
  pointsLabel = 'Points / Stay',
  pointsSaverLabel = 'Points Saver',
  ampRequest,
  soldOutLabel,
  rateUnavailableLabel,
  isSpecialRateSearch = false,
  isFlexibleDateSearch = false,
  openingDate,
  callToBookLabel,
  openingSoonLabel,
  openingSoonDateFormat = 'MMMM YYYY',
  isBookable,
  maxOccupancyLabel,
  adultsOnlyLabel,
  isAdultsOnly,
  childCount = 0,
  viewRatesURL = '',
  viewRatesLabel = 'View Rates',
}) => {
  const pageContext = usePageContext();
  const currentLocale = pageContext?.currentLocale || 'en-US';
  const rateRequestValue = useMemo(() => getRateRequestValue(ampRequest), [ampRequest]);

  // Build searchQueryData from ampRequest (same format as searchQueryState in MPropertyCardV0)
  const searchQueryData = useMemo(() => {
    return buildSearchQueryDataFromAmpRequest(ampRequest);
  }, [ampRequest]);

  // Get rate blocks using the same logic as MPropertyCardV0
  const rates = edge?.node?.rates;

  const currRateNode = useMemo(() => {
    if (!rates || rates.length === 0) return null;
    return getCurrentPriceRateBlock({ search: { options: searchQueryData.options } } as any, rates);
  }, [searchQueryData, rates]);

  const pointsRateBlock = useMemo(() => getRateBlock(rates, 'points'), [rates]);
  const eeoRateBlock = useMemo(() => getRateBlock(rates, EEO_TYPE), [rates]);

  // Check if opening date is in the future (matching MPropertyCardV0)
  const isFutureDate = useMemo(() => {
    if (!openingDate || typeof openingDate !== 'string') return false;
    return isDateAfterToday(openingDate);
  }, [openingDate]);

  // Check if rates are present (matching MPropertyCardV0)
  const isRatesPresent = useMemo(() => {
    return Boolean(
      currRateNode?.rateModes?.lowestAverageRate?.amount || pointsRateBlock?.rateModes?.pointsPerUnit?.points
    );
  }, [currRateNode, pointsRateBlock]);

  // Check unavailability conditions - EXACTLY matching MPropertyCardV0
  const hotelCodeUnavailable = useMemo(() => {
    return getHotelCodeUnavailable(currRateNode, isFlexibleDateSearch, pointsRateBlock);
  }, [currRateNode, isFlexibleDateSearch, pointsRateBlock]);

  const { isHotelUnavailable, isHotelMaxOccupancyExceeded } = useMemo(() => {
    return hotelUnavailableOrMaxOccupied(currRateNode, pointsRateBlock, eeoRateBlock, false, openingDate);
  }, [currRateNode, pointsRateBlock, eeoRateBlock, isFlexibleDateSearch, openingDate]);

  // Parse opening date string to DateObjectType for formatOpeningSoonDate
  // The function accepts { year, month, day } objects despite the Moment type definition
  const openingDateObject = useMemo(() => {
    if (!openingDate || typeof openingDate !== 'string') return null;
    const year = parseInt(openingDate.substring(0, 4), 10);
    const month = parseInt(openingDate.substring(5, 7), 10);
    const day = parseInt(openingDate.substring(8, 10), 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    return { year, month, day };
  }, [openingDate]);

  // Price calculation hooks - must be before early returns per React Rules of Hooks
  const eeoInfo = useMemo(() => getEEOPricingInfo(edge?.node?.rates, rateRequestValue), [edge, rateRequestValue]);
  const lowestRate = eeoInfo?.displayRate || null;
  const pointsInfo = useMemo(() => getPointsInfo(edge?.node?.rates, rateRequestValue), [edge, rateRequestValue]);
  const pointsValue = pointsInfo.points;
  const isPointsSaver = pointsInfo.isPointsSaver;
  let conversionFactor = 1;
  const currentConversionRateArr = useMemo(() => {
    return edge?.node?.property?.currentConversionRateArr || [];
  }, [edge]);
  const priceInfo = useMemo((): PriceInfo | null => {
    const amountData = lowestRate?.amountPlusMandatoryFees?.amount
      ? lowestRate.amountPlusMandatoryFees
      : lowestRate?.amount;

    if (amountData?.amount && amountData?.currency && typeof amountData?.decimalPoint === 'number') {
      const numericPrice = createDecimalValue(amountData.amount, amountData.decimalPoint);
      conversionFactor = amountData.currency
        ? getRateConversionFactor(currentConversionRateArr, amountData.currency)
        : 1;
      const price = formatNumberByLocale(getExchangeConversionRate(numericPrice, conversionFactor), currentLocale);
      return { price, currency: amountData.currency, numericPrice };
    }

    const fallbackPrice = priceDisplay || edge?.node?.property?.basicInformation?.priceDisplay;
    if (fallbackPrice) {
      return { price: fallbackPrice, currency: '', numericPrice: 0 };
    }

    return null;
  }, [lowestRate, edge, priceDisplay, currentConversionRateArr]);

  const strikeThroughPriceInfo = useMemo((): PriceInfo | null => {
    if (!eeoInfo?.showStrikeThrough || !eeoInfo?.strikeThroughRate) return null;

    const strikeThroughRate = eeoInfo.strikeThroughRate;
    const amountData = strikeThroughRate?.amountPlusMandatoryFees?.amount
      ? strikeThroughRate.amountPlusMandatoryFees
      : strikeThroughRate?.amount;

    if (amountData?.amount && amountData?.currency && typeof amountData?.decimalPoint === 'number') {
      conversionFactor = amountData.currency
        ? getRateConversionFactor(currentConversionRateArr, amountData.currency)
        : 1;
      const numericPrice = createDecimalValue(amountData.amount, amountData.decimalPoint);
      const price = formatNumberByLocale(getExchangeConversionRate(numericPrice, conversionFactor), currentLocale);
      return { price, currency: amountData.currency, numericPrice };
    }

    return null;
  }, [eeoInfo]);

  const mandatoryFeeInfo = useMemo(() => {
    const mandatoryFees = lowestRate?.mandatoryFees;
    if (!mandatoryFees?.amount || mandatoryFees.amount === 0) return null;

    const basicInfo = edge?.node?.property?.basicInformation;
    const feeTypes = identifyMandatoryFeeType(basicInfo);
    const { isLACountyFee, isMarketingFee } = feeTypes;

    const feeLabel = getFeeLabel(feeTypes);
    const feeDescription = getFeeDescription(basicInfo);
    const modalTitle = getFeeModalTitle(feeTypes);

    const feeAmount = formatMandatoryFeeForDisplay(mandatoryFees.amount, mandatoryFees.decimalPoint || 0);
    const currency = mandatoryFees.currency || 'USD';
    if (feeLabel !== '') {
      return {
        text: `Includes ${feeAmount} ${currency} ${feeLabel} Fee`,
        amount: feeAmount,
        currency,
        feeLabel,
        feeType: `${feeLabel} Fee`,
        modalTitle,
        description: feeDescription,
        isLACountyFee,
        isMarketingFee,
      };
    } else {
      return null;
    }
  }, [lowestRate, edge]);

  const [isFeeModalOpen, setIsFeeModalOpen] = useState(false);

  const handleFeeInfoClick = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFeeModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsFeeModalOpen(false);
  }, []);

  // Determine if mandatory fee should be shown
  // Following the reference: exclude LA County fee and Marketing fee from mandatory fee display in points mode
  const shouldShowMandatoryFee =
    mandatoryFeeInfo && !mandatoryFeeInfo.isLACountyFee && !mandatoryFeeInfo.isMarketingFee;


  // Edge case rendering - matching MPropertyCardV0 priority order
  // 1. Opening Soon: isFutureDate && (isFlexibleDateSearch || !isRatesPresent)
  if (isFutureDate && (isFlexibleDateSearch || !isRatesPresent) && openingSoonLabel && openingDateObject) {
    const formattedDate = formatOpeningSoonDate(
      openingSoonLabel,
      openingDateObject as any,
      openingSoonDateFormat,
      currentLocale
    );
    return (
      <Styled.Container showDivider={showDivider}>
        <span className="t-font-alt-xs">{formattedDate}</span>
      </Styled.Container>
    );
  }

  // 2. isHotelCodeUnavailable → Always "Sold Out"
  if (hotelCodeUnavailable && soldOutLabel) {
    return (
      <Styled.Container showDivider={showDivider}>
        <span className="t-font-alt-xs">{soldOutLabel}</span>
      </Styled.Container>
    );
  }

  // 3. isHotelUnavailable → "Rate Unavailable" if special rate, else "Sold Out"
  if (isHotelUnavailable && (soldOutLabel || rateUnavailableLabel)) {
    const label = isSpecialRateSearch ? rateUnavailableLabel : soldOutLabel;
    return (
      <Styled.Container showDivider={showDivider}>
        <span className="t-font-alt-xs">{label}</span>
      </Styled.Container>
    );
  }

  // 4. Call to Book: !isBookable
  if (isBookable === false && callToBookLabel) {
    return (
      <Styled.Container showDivider={showDivider}>
        <span className="t-font-alt-xs">{callToBookLabel}</span>
      </Styled.Container>
    );
  }

  // 5. Adults Only: isAdultsOnly && childCount > 0
  if (isAdultsOnly && childCount > 0 && adultsOnlyLabel) {
    return (
      <Styled.Container showDivider={showDivider}>
        <span className="t-font-alt-xs">{adultsOnlyLabel}</span>
      </Styled.Container>
    );
  }

  // 6. Max Occupancy: isHotelMaxOccupancyExceeded
  if (isHotelMaxOccupancyExceeded && maxOccupancyLabel) {
    return (
      <Styled.Container showDivider={showDivider}>
        <span className="t-font-alt-xs">{maxOccupancyLabel}</span>
      </Styled.Container>
    );
  }

  // For points mode, show both points AND cash price side by side
  if (displayMode === 'points') {
    // If no points available, fall back to cash-only display
    if (!pointsValue && !priceInfo) return null;

    return (
      <Styled.Container showDivider={showDivider}>
        {/* Points Saver label - shown when P17 rate is available */}
        {pointsValue && isPointsSaver && (
          <Styled.PointsSaverLabel className="t-label-alt-xs">{pointsSaverLabel}</Styled.PointsSaverLabel>
        )}

        {/* Points display - primary when in points mode */}
        {pointsValue && (
          <Styled.RateBlock>
            {isPointsSaver ? (
              <Styled.PointsSaverPriceText className="m-price-s">
                {formatPointsValue(pointsValue)}
              </Styled.PointsSaverPriceText>
            ) : (
              <Styled.PriceText className="m-price-s">{formatPointsValue(pointsValue)}</Styled.PriceText>
            )}
            <Styled.CurrencyText className="t-font-xs t-font-weight-m">{pointsLabel}</Styled.CurrencyText>
          </Styled.RateBlock>
        )}

        {/* Mandatory fee display for points mode - format: "+ {amount} {currency} daily {feeType}" */}
        {shouldShowMandatoryFee && (
          <Styled.MandatoryFeeText className="t-font-xs d-inline-flex align-items-center text-left">
            + {mandatoryFeeInfo.amount} {mandatoryFeeInfo.currency} daily {mandatoryFeeInfo.feeType}
            <Styled.InfoIconWrapper
              aria-label="Fee information"
              role="button"
              tabIndex={0}
              onClick={handleFeeInfoClick}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleFeeInfoClick(e);
                }
              }}
            >
              <AIcon icon="icon-information" size={AIconSize.S} className="a-icon-muted" aria-hidden="true" />
            </Styled.InfoIconWrapper>
          </Styled.MandatoryFeeText>
        )}

        {/* Cash price display - secondary, shown alongside points */}
        {priceInfo && (
          <Styled.CashPriceText className="t-label-xs">
            <Styled.SrOnlySeparator>, </Styled.SrOnlySeparator>
            {priceInfo.currency
              ? `${priceInfo.price} ${priceInfo.currency} / Night${shouldShowMandatoryFee ? ' includes daily fee' : ''}`
              : `${priceInfo.price} / Night`}
            <Styled.SrOnlySeparator>, </Styled.SrOnlySeparator>
          </Styled.CashPriceText>
        )}

        <FeeModal
          isOpen={isFeeModalOpen}
          onClose={handleCloseModal}
          modalTitle={mandatoryFeeInfo?.modalTitle || ''}
          description={mandatoryFeeInfo?.description || null}
          viewRatesURL={viewRatesURL}
          viewRatesLabel={viewRatesLabel}
        />
      </Styled.Container>
    );
  }

  // Cash mode - with EEO strikethrough support
  if (!priceInfo) return null;

  return (
    <Styled.Container showDivider={showDivider}>
      {/* Strikethrough price display for EEO rates */}
      {strikeThroughPriceInfo && (
        <Styled.StrikeThroughPrice className="t-label-xs m-strikethrough">
          {strikeThroughPriceInfo.price}
        </Styled.StrikeThroughPrice>
      )}
      <Styled.RateBlock>
        <Styled.SrOnlySeparator>, </Styled.SrOnlySeparator>
        <Styled.PriceText className="m-price-s">{priceInfo.price}</Styled.PriceText>
        {priceInfo.currency && (
          <Styled.CurrencyText className="t-font-xs t-font-weight-m">{`${priceInfo.currency} / Night`}</Styled.CurrencyText>
        )}
        <Styled.SrOnlySeparator>, </Styled.SrOnlySeparator>
      </Styled.RateBlock>
      {mandatoryFeeInfo && (
        <Styled.MandatoryFeeText className="t-font-xs d-inline-flex align-items-center text-left">
          {mandatoryFeeInfo.text}
          <Styled.InfoIconWrapper aria-label="Fee information" type="button" onClick={handleFeeInfoClick}>
            <AIcon icon="icon-information" size={AIconSize.S} aria-hidden="true" />
          </Styled.InfoIconWrapper>
        </Styled.MandatoryFeeText>
      )}

      <FeeModal
        isOpen={isFeeModalOpen}
        onClose={handleCloseModal}
        modalTitle={mandatoryFeeInfo?.modalTitle || ''}
        description={mandatoryFeeInfo?.description || null}
        viewRatesURL={viewRatesURL}
        viewRatesLabel={viewRatesLabel}
      />
    </Styled.Container>
  );
};
